/**
 * User Types - Tipos TypeScript para dados de usuario e sessao
 * @module features/chat/types/user.types
 *
 * Este modulo define os tipos para coleta de dados de identificacao
 * do usuario durante o fluxo de assessment conversacional.
 */

// =============================================================================
// CORE TYPES
// =============================================================================

/**
 * Tipo de assessment disponivel no sistema
 * - tech: Assessment tecnico para desenvolvedores
 * - governance: Assessment de governanca para gestores
 */
export type AssessmentType = 'tech' | 'governance';

/**
 * Dados do usuario coletados durante o chat conversacional
 *
 * Campos opcionais sao coletados progressivamente durante a conversa.
 * Os campos de consentimento sao obrigatorios para LGPD compliance.
 *
 * @example
 * ```typescript
 * const user: UserData = {
 *   name: 'Maria Silva',
 *   email: 'maria@empresa.com',
 *   company: 'Tech Corp',
 *   role: 'CTO',
 *   consentGiven: true,
 *   consentTimestamp: new Date(),
 * };
 * ```
 */
export interface UserData {
  /** Nome completo do usuario (opcional ate ser coletado) */
  name: string | undefined;

  /** Email profissional do usuario (opcional ate ser coletado) */
  email: string | undefined;

  /** Nome da empresa do usuario (opcional) */
  company: string | undefined;

  /** Cargo/funcao do usuario na empresa (opcional) */
  role: string | undefined;

  // US-033: Campos HubSpot
  /** Valor HubSpot do cargo selecionado */
  cargo: string | null;

  /** Valor HubSpot da area selecionada */
  area: string | null;

  /** Valor do tamanho da empresa */
  tamanhoEmpresa: string | null;

  /** Array de valores HubSpot das dores selecionadas */
  dores: string[] | null;

  /**
   * Indica se o usuario deu consentimento para coleta de dados (LGPD)
   * Este campo e obrigatorio e deve ser true para prosseguir
   */
  consentGiven: boolean;

  /**
   * Timestamp do momento em que o consentimento foi dado
   * Undefined enquanto consentimento nao for dado
   */
  consentTimestamp: Date | undefined;
}

/**
 * Estado da sessao de assessment do usuario
 *
 * Armazena o contexto completo de uma sessao de assessment,
 * incluindo dados do usuario e metadados de tempo.
 *
 * @example
 * ```typescript
 * const session: SessionState = {
 *   sessionId: 'sess_abc123',
 *   userData: INITIAL_USER_DATA,
 *   assessmentType: 'tech',
 *   startedAt: new Date(),
 *   completedAt: undefined,
 *   messages: [],
 * };
 * ```
 */
export interface SessionState {
  /** Identificador unico da sessao (UUID) */
  sessionId: string;

  /** Dados do usuario associados a esta sessao */
  userData: UserData;

  /**
   * Tipo de assessment selecionado pelo usuario
   * Undefined ate o usuario selecionar
   */
  assessmentType: AssessmentType | undefined;

  /** Data/hora de inicio da sessao */
  startedAt: Date;

  /**
   * Data/hora de conclusao da sessao
   * Undefined enquanto sessao estiver em andamento
   */
  completedAt: Date | undefined;

  /**
   * Historico de mensagens do chat (T003-FE)
   * Persistido no LocalStorage para recuperacao de sessao
   */
  messages: import('./chat.types').ChatMessage[];

  /**
   * URL do relatorio gerado (T003-INT)
   * Undefined ate o relatorio ser gerado com sucesso
   */
  reportUrl?: string;

  /**
   * Indica se houve falha na geracao do relatorio (T003-INT)
   * Usado para exibir opcao de regenerar na tela de resultados
   */
  reportGenerationFailed?: boolean;

  /**
   * Data/hora em que o relatorio foi gerado (T003-INT)
   * Undefined ate o relatorio ser gerado com sucesso
   */
  reportGeneratedAt?: Date;
}

// =============================================================================
// UTILITY TYPES
// =============================================================================

/**
 * Versao de UserData com campos obrigatorios preenchidos
 *
 * Usado apos validacao, quando os campos essenciais foram coletados
 * e o consentimento foi dado (consentGiven = true literal).
 *
 * Campos obrigatorios:
 * - name: string (nao pode ser undefined)
 * - email: string (nao pode ser undefined)
 * - consentGiven: true (literal, nao boolean)
 * - consentTimestamp: Date (nao pode ser undefined)
 *
 * Campos que permanecem opcionais:
 * - company: string | undefined
 * - role: string | undefined
 *
 * @example
 * ```typescript
 * const validUser: RequiredUserFields = {
 *   name: 'Joao',
 *   email: 'joao@email.com',
 *   company: undefined, // opcional
 *   role: undefined,    // opcional
 *   consentGiven: true, // obrigatorio ser true
 *   consentTimestamp: new Date(), // obrigatorio
 * };
 * ```
 */
export interface RequiredUserFields {
  /** Nome completo (obrigatorio) */
  name: string;

  /** Email profissional (obrigatorio) */
  email: string;

  /** Nome da empresa (permanece opcional) */
  company: string | undefined;

  /** Cargo/funcao (permanece opcional) */
  role: string | undefined;

  /** Consentimento dado - deve ser true literal */
  consentGiven: true;

  /** Timestamp do consentimento (obrigatorio) */
  consentTimestamp: Date;
}

// =============================================================================
// INITIAL STATE CONSTANTS
// =============================================================================

/**
 * Estado inicial dos dados do usuario
 *
 * Todos os campos opcionais sao undefined e consentimento e false.
 * Usado para inicializar o estado antes da coleta de dados.
 */
export const INITIAL_USER_DATA: UserData = {
  name: undefined,
  email: undefined,
  company: undefined,
  role: undefined,
  cargo: null,
  area: null,
  tamanhoEmpresa: null,
  dores: null,
  consentGiven: false,
  consentTimestamp: undefined,
};

/**
 * Estado inicial da sessao
 *
 * SessionId vazio, userData inicial, sem assessment type selecionado.
 * A data de inicio e definida no momento da criacao real da sessao.
 */
export const INITIAL_SESSION_STATE: SessionState = {
  sessionId: '',
  userData: INITIAL_USER_DATA,
  assessmentType: undefined,
  startedAt: new Date(0), // Placeholder - sera definido na criacao real
  completedAt: undefined,
  messages: [],
};
